import { createAction, createSlice } from '@reduxjs/toolkit';
import { setSharedValue, setFilter, setTableLoadingState } from '@/pages/shared/shared.reducer';
import { runApprovalSummaryMutation } from '@/pages/approval-summary/approval-summary.query';
import { notifyUnlessUnauthorized, isUnauthorizedError } from '@/pages/shared/rtk-query-helpers';
import { openSuccessNotification, openErrorNotification } from '@/notification';
import { getNotifications } from '@/pages/login/login.reducer';
import { APPROVAL_STATUS_FILTER_OPTIONS } from '@/pages/approval-summary/approval-summary.statuses';

const APPROVAL_SUMMARY_VALUE_SET = "APPROVAL_SUMMARY_VALUE_SET";

const initialState = {
    authors: {
        items: [],
        loading: false
    },
    approvalDocumentFileStatuses: {
        items: APPROVAL_STATUS_FILTER_OPTIONS,
        loading: false
    },
    sites: {
        items: [],
        loading: false
    },
    approvers: {
        items: [],
        loading: false
    }
}

// Replaces immutability-helper `update.set`: assigns a dotted path, creating missing objects.
const setPathValue = (draft, path, value) => {
    const segments = String(path).split('.');
    let target = draft;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];

        if (target[segment] === null || typeof target[segment] !== 'object') {
            target[segment] = {};
        }

        target = target[segment];
    }

    target[segments[segments.length - 1]] = value;
};

// Legacy action type string is preserved so existing dispatch sites keep working.
export const setValue = createAction(APPROVAL_SUMMARY_VALUE_SET, (key, value) => ({
    payload: { key, value }
}));

const approvalSummarySlice = createSlice({
    name: 'approvalSummary',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(setValue, (draft, action) => {
            setPathValue(draft, `${action.payload.key}`, action.payload.value);
        });
    }
});

const approvalSummaryReducer = approvalSummarySlice.reducer;

export const loadAuthors = (model) => {
    return (dispatch) => {
        dispatch(setValue('authors.loading', true));
        runApprovalSummaryMutation(dispatch, 'getAuthorList', model)
            .then(payload => {
                dispatch(setValue(`authors.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load authors. Please reload page`);
            });
        dispatch(setValue('authors.loading', false));
    }
}

export const loadSites = (model) => {
    return (dispatch) => {
        dispatch(setValue('sites.loading', true));
        runApprovalSummaryMutation(dispatch, 'getSitesList', model)
            .then(payload => {
                dispatch(setValue(`sites.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load sites. Please reload page`);
            });
        dispatch(setValue('sites.loading', false));
    }
}

export const loadStatuses = (model) => {
    return (dispatch) => {
        dispatch(setValue('approvalDocumentFileStatuses.loading', true));
        runApprovalSummaryMutation(dispatch, 'getStatusesList', model)
            .then(payload => {
                dispatch(setValue('approvalDocumentFileStatuses.items', payload.map(item => ({
                    statusId: item.id,
                    status: item.name
                }))));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load statuses. Please reload page`);
            })
            .finally(() => {
                dispatch(setValue('approvalDocumentFileStatuses.loading', false));
            });
    }
}

export const loadApprovers = (model) => {
    return (dispatch) => {
        dispatch(setValue('approvers.loading', true));
        runApprovalSummaryMutation(dispatch, 'getApproversList', model)
            .then(payload => {
                dispatch(setValue(`approvers.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load approvers. Please reload page`);
            });
        dispatch(setValue('approvers.loading', false));
    }
}

export const approveDocuments = (items, count, comment) => {
    return async (dispatch) => {
        const documentFileIds = items.map(item => item.documentFileId);
        dispatch(setTableLoadingState('approvalSummaryTable', true));
        dispatch(setTableLoadingState('cartManagementTable', true));

        try {
            await runApprovalSummaryMutation(dispatch, 'approveDocuments', { Ids: documentFileIds, comment: comment });

            dispatch(setFilter('approvalSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setFilter('cartManagementTable', 'key', Math.floor(Math.random() * Math.floor(2500))))

            items.forEach(item => {
                const revisionNumber = item?.revisionNumber || item?.documentFile?.revisionNumber || "";
                openSuccessNotification(`${item.documentNumber} Revision ${revisionNumber} was approved`);
            });
            dispatch(getNotifications());
            dispatch(setSharedValue('approvalSummaryTable.count', count - items.length))
            dispatch(loadSites({}));
        } catch (error) {
            notifyUnlessUnauthorized(error, "Some problem in approve");
        } finally {
            dispatch(setTableLoadingState('approvalSummaryTable', false));
            dispatch(setTableLoadingState('cartManagementTable', false));
        }
    }
}

export const rollbackRevision = (documentFileIds, documentFiles, comment) => {
    return async (dispatch) => {
        dispatch(setTableLoadingState('approvalSummaryTable', true));
        dispatch(setTableLoadingState('cartManagementTable', true));

        try {
            const payload = await runApprovalSummaryMutation(dispatch, 'rollbackRevision', { Ids: documentFileIds, comment });

            dispatch(setFilter('approvalSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setFilter('cartManagementTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            openSuccessNotification(payload.message);
            dispatch(getNotifications());
            dispatch(loadSites({}));
        } catch (error) {
            if (!isUnauthorizedError(error)) {
                const failedDocumentNumber = documentFiles?.[0]?.documentNumber || "selected document(s)";
                openErrorNotification(`Can't rollback ${failedDocumentNumber}`)
            }
        } finally {
            dispatch(setTableLoadingState('approvalSummaryTable', false));
            dispatch(setTableLoadingState('cartManagementTable', false));
        }
    }
}

export const rejectDocument = (items, comment) => {
    return async (dispatch) => {
        const documentFileIds = items.filter(item => item.documentFileStatus != "Rejected").map(item => item.documentFileId);
        dispatch(setTableLoadingState('approvalSummaryTable', true));
        dispatch(setTableLoadingState('cartManagementTable', true));

        try {
            await runApprovalSummaryMutation(dispatch, 'rejectDocument', { Ids: documentFileIds, comment: comment });

            dispatch(setFilter('approvalSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setFilter('cartManagementTable', 'key', Math.floor(Math.random() * Math.floor(2500))))

            items.forEach(element => {
                if (element.documentFileStatus != "Rejected") {
                    openSuccessNotification(`Document ${element.documentNumber} was rejected`);
                    dispatch(getNotifications());
                }
                else {
                    openErrorNotification(`Document ${element.documentNumber} is already reject`)
                }
            });
            dispatch(loadSites({}));
        } catch (error) {
            notifyUnlessUnauthorized(error, "Some problem in reject");
        } finally {
            dispatch(setTableLoadingState('approvalSummaryTable', false));
            dispatch(setTableLoadingState('cartManagementTable', false));
        }
    }
}

export const reassignRevision = (documentFiles, approver, siteId) => {
    return async (dispatch) => {
        dispatch(setTableLoadingState('approvalSummaryTable', true));
        dispatch(setTableLoadingState('cartManagementTable', true));
        const documentFileIds = documentFiles.map(item => item.documentFileId);

        // Make sure we have a valid approverUserId
        const approverUserId = approver?.userId;
        if (!approverUserId) {
            openErrorNotification("Invalid approver selected");
            dispatch(setTableLoadingState('approvalSummaryTable', false));
            dispatch(setTableLoadingState('cartManagementTable', false));
            return;
        }

        try {
            await runApprovalSummaryMutation(dispatch, 'reassignRevision', { documentFileIds, approverUserId, siteId });

            dispatch(setFilter('approvalSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setFilter('cartManagementTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            documentFiles.forEach(item => {
                openSuccessNotification(`Reassign revision done for ${item.documentNumber}`);
            })
            dispatch(getNotifications());
        } catch (error) {
            if (!isUnauthorizedError(error)) {
                documentFiles.forEach(item => {
                    openErrorNotification(`Can't reassign revision for document ${item.documentNumber}`)
                })
            }
        } finally {
            dispatch(setTableLoadingState('approvalSummaryTable', false));
            dispatch(setTableLoadingState('cartManagementTable', false));
        }
    }
}

export default approvalSummaryReducer;
